import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { isInStandaloneMode } from '../utils/pwaUtils';

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; visibility: hidden; }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const scaleIn = keyframes`
  from { transform: scale(0.9); }
  to { transform: scale(1); }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const SplashScreenContainer = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  animation: ${({ $isVisible }) => ($isVisible ? fadeIn : fadeOut)} 0.5s ease-out;
  animation-fill-mode: forwards;
  pointer-events: none;
`;

const LogoContainer = styled.div`
  animation: ${scaleIn} 0.5s ease-out;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
`;

const Logo = styled.img`
  width: 120px;
  height: 120px;
  object-fit: contain;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 4px solid #2563eb;
  animation: ${spin} 1s linear infinite;
`;

const AppName = styled.h1`
  font-size: 1.75rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  background: linear-gradient(90deg, #2563eb, #3b82f6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
`;

const AppDescription = styled.p`
  font-size: 1rem;
  color: #6b7280;
  margin: 0.5rem 0 0;
  font-weight: 500;
`;

const SplashScreen: React.FC = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if app is in standalone mode
    setIsStandalone(isInStandaloneMode());
    
    // Hide splash screen after 2 seconds
    const timer = setTimeout(() => {
      setIsVisible(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  // Don't show splash screen if not in standalone mode
  if (!isStandalone) return null;

  return (
    <SplashScreenContainer $isVisible={isVisible}>
      <LogoContainer>
        <Logo 
          src="/images/logo-ex.png" 
          alt="FMS Logo"
          onError={(e) => {
            // Fallback to mini logo if main logo fails to load
            e.currentTarget.src = "/images/logo-mini.png";
          }} 
        />
        <AppName>Factory Management</AppName>
        <AppDescription>Loading...</AppDescription>
        <Spinner />
      </LogoContainer>
    </SplashScreenContainer>
  );
};

export default SplashScreen;
